import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  StarIcon,
  ShareIcon,
  ArrowLeftIcon,
  FlagIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const ForumDetails = () => {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [postingReply, setPostingReply] = useState(false);

  // Fetch thread and replies
  useEffect(() => {
    const fetchThread = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.forums.getThread(id);
        setThread(data);
      } catch (err) {
        setError('Failed to load thread.');
      } finally {
        setLoading(false);
      }
    };
    fetchThread();
  }, [id]);

  // Post a reply
  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setPostingReply(true);
    try {
      await api.forums.addReply(id, { content: replyContent });
      setReplyContent('');
      // Refresh thread
      const data = await api.forums.getThread(id);
      setThread(data);
    } catch (err) {
      alert('Failed to post reply.');
    } finally {
      setPostingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Thread not found.'}</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Forums
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent relative">
      {/* Noise Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.015] mix-blend-soft-light pointer-events-none">
        <svg className="w-full h-full">
          <filter id="noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#noise)" />
        </svg>
      </div>

      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/40 via-purple-100/40 to-pink-100/40 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-200/30 via-transparent to-transparent dark:from-indigo-800/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 mb-8"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Forums
        </button>

        {/* Thread Header */}
        <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 mb-8 border border-gray-200/20 dark:border-gray-700/20">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{thread.title}</h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                <span className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  {thread.category}
                </span>
                <span className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Posted {new Date(thread.created_at).toLocaleString()}
                </span>
                <span className="flex items-center">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                  {thread.replies ? thread.replies.length : 0} replies
                </span>
              </div>
            </div>
            
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Thread Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Original Post */}
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{thread.author_name}</h3>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{new Date(thread.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-4 text-gray-600 dark:text-gray-300">{thread.content}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(Array.isArray(thread.tags) ? thread.tags : []).map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Replies */}
            <div className="space-y-4">
              {Array.isArray(thread.replies) && thread.replies.length > 0 ? (
                thread.replies.map((reply, index) => (
                <div
                    key={reply.id || index}
                  className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">{reply.author_name}</h3>
                        </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{new Date(reply.created_at).toLocaleString()}</span>
                      </div>
                      <p className="mt-4 text-gray-600 dark:text-gray-300">{reply.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 dark:text-gray-400">No replies yet.</div>
              )}
            </div>

            {/* Reply Form */}
            <form onSubmit={handleReply} className="mt-8 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Post a Reply</h3>
              <textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
                placeholder="Write your reply..."
                required
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={postingReply}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {postingReply ? 'Posting...' : 'Post Reply'}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column - Thread Info */}
          <div className="space-y-8">
            <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 border border-gray-200/20 dark:border-gray-700/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Thread Info</h2>
              <div className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                <div><span className="font-medium">Category:</span> {thread.category}</div>
                <div><span className="font-medium">Author:</span> {thread.author_name}</div>
                <div><span className="font-medium">Created:</span> {new Date(thread.created_at).toLocaleString()}</div>
                <div><span className="font-medium">Updated:</span> {new Date(thread.updated_at).toLocaleString()}</div>
                <div><span className="font-medium">Thread ID:</span> {thread.id}</div>
                <div><span className="font-medium">Replies:</span> {thread.replies_count}</div>
                <div><span className="font-medium">Views:</span> {thread.views_count}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForumDetails;